import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Dialog, IconButton, Box } from '@mui/material';

interface FullscreenImageViewerProps {
  open: boolean;
  imageUrl: string;
  onClose: () => void;
}

const FullscreenImageViewer: React.FC<FullscreenImageViewerProps> = ({
  open,
  imageUrl,
  onClose
}) => {
  const [zoom, setZoom] = useState<number>(1);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const isZoomed = zoom > 1;

  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const updateZoom = (delta: number) => {
    const newZoom = Math.max(1, Math.min(4, zoom + delta));

    if (newZoom === 1) {
      setPosition({ x: 0, y: 0 });
    } else if (newZoom !== zoom) {
      const ratio = newZoom / zoom;
      setPosition((prev) => ({ x: prev.x * ratio, y: prev.y * ratio }));
    }

    setZoom(newZoom);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isZoomed) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleClose = () => {
    resetView();
    onClose();
  };

  const getCursorStyle = () => {
    if (!isZoomed) return 'zoom-in';
    return isDragging ? 'grabbing' : 'grab';
  };

  const controls = [
    { icon: ZoomInIcon, onClick: () => updateZoom(0.5), disabled: zoom >= 4 },
    { icon: ZoomOutIcon, onClick: () => updateZoom(-0.5), disabled: zoom <= 1 },
    { icon: RestartAltIcon, onClick: resetView, disabled: !isZoomed },
    { icon: CloseIcon, onClick: handleClose, disabled: false }
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          margin: 0,
          width: '100vw',
          height: '100vh',
          maxWidth: '100%',
          maxHeight: '100%'
        }
      }}
    >
      <Box position="absolute" top={16} right={16} zIndex={1} gap={1}>
        {controls.map(({ icon: Icon, onClick, disabled }, i) => (
          <IconButton
            key={i}
            onClick={onClick}
            disabled={disabled}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:disabled': { color: 'rgba(255,255,255,0.3)' }
            }}
          >
            <Icon />
          </IconButton>
        ))}
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="100%"
        height="100%"
        sx={{ cursor: getCursorStyle() }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onWheel={(e) => {
          if (e.ctrlKey) {
            e.preventDefault();
            e.stopPropagation();
            if (zoom > 1 || e.deltaY < 0) {
              updateZoom(e.deltaY > 0 ? -0.5 : 0.5);
            }
          }
        }}
        onClick={() => !isZoomed && updateZoom(0.5)}
      >
        <img
          src={imageUrl}
          alt="Fullscreen"
          draggable={false}
          style={{
            maxWidth: '90%',
            maxHeight: '90%',
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transition: isDragging ? 'none' : 'transform 0.3s ease',
            userSelect: 'none',
            pointerEvents: 'none'
          }}
        />
      </Box>
    </Dialog>
  );
};

export default FullscreenImageViewer;
